<template>
  <base-material-card
    color="primary"
    icon="mdi-file-table-outline"
    inline
    class="px-5 py-5"
  >
    <template v-slot:after-heading>
      <div class="display-1 font-weight-light">
        Novo Laudo Express
      </div>
    </template>

    <v-form class="pt-5">
      <v-row class="mx-4">
        <v-col cols="12">
          <v-alert
            dense
            outlined
            type="info"
            color="blue"
          >
            <div>
              <a href="javascript:void(0)" @click="getArquivoModelo()">Clique aqui</a> para baixar o arquivo modelo da importação.
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <v-row
        align="center"
        no-gutters
        class="mx-4"
      >
        <v-col cols="12">
          <v-text-field
            id="titulo-montagem-ais-incremental"
            v-model="titulo"
            label="Título*"
            outlined
            persistent-hint
            :error-messages="fieldErrors.titulo"
          />
        </v-col>
      </v-row>

      <v-row
        align="center"
        no-gutters
        class="mx-4"
      >
        <v-col cols="12" md="4">
          <date-picker
            id="data-base-laudo-montagem-ais-incremental"
            v-model="dataBaseLaudo"
            label="Data Base do Laudo*"
            type="date"
            rules="required"
            outlined
            hint="Informe a data base do laudo para o processamento."
            persistent-hint
          />
        </v-col>
      </v-row>

      <v-row
        align="center"
        no-gutters
        class="mx-4"
      >
        <v-col cols="12">
          <v-file-input
            v-model="arquivoUploadFileObj"
            id="arquivo-montagem-ais-incremental"
            label="Arquivo do modelo (.xlsx)*"
            placeholder="Selecione o arquivo .xlsx"
            outlined
            accept=".xlsx"
            truncate-length="35"
            loader-height="4"
            :loading="arquivoUploadUploading"
            :success="!!(arquivoXlsxId && arquivoUploadFileObj)"
            :error-messages="fieldErrors.arquivo_xlsx_id"
            @change="uploadNewFile"
            @click:clear="clearArquivoUpload"
          />
        </v-col>
      </v-row>

      <v-row
        v-if="arquivoXlsxId"
        class="mx-4 mt-n2"
      >
        <v-col cols="12">
          <v-alert
            dense
            outlined
            type="success"
          >
            Arquivo enviado com sucesso e pronto para processamento.
          </v-alert>
        </v-col>
      </v-row>
    </v-form>
  </base-material-card>
</template>

<script>
import dayjs from 'dayjs';
import MontagensAisIncrementaisService from '@/services/MontagensAisIncrementaisService';

export default {
  name: 'MontagensAisIncrementaisParametrosUpload',

  components: {
    DatePicker: () => import('@/components/general/DatePicker.vue')
  },

  data() {
    return {
      titulo: '',
      dataBaseLaudo: dayjs().format('YYYY-MM-DD'),
      arquivoXlsxId: null,
      arquivoUploadFileObj: null,
      arquivoUploadUploading: false,
      fieldErrors: {
        titulo: [],
        data_base_laudo: [],
        arquivo_xlsx_id: []
      }
    };
  },

  watch: {
    titulo() {
      this.fieldErrors.titulo = [];
      this.notifyValidationChange();
    },

    dataBaseLaudo() {
      this.fieldErrors.data_base_laudo = [];
      this.notifyValidationChange();
    },

    arquivoXlsxId() {
      this.notifyValidationChange();
    },

    arquivoUploadUploading() {
      this.notifyValidationChange();
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.notifyValidationChange();
    });
  },

  methods: {
    getArquivoModelo() {
      this.$toast.info('Processando...', '', { position: 'topRight', timeout: 1250 });

      MontagensAisIncrementaisService.getArquivoModelo()
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'modelo_laudo_express.xlsx');
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          this.$toast.error('Erro ao baixar arquivo modelo.', '', { position: 'topRight' });
          console.error(error);
        });
    },

    exportData() {
      const isTituloValido = this.validateTitulo(true);
      const isDataBaseValida = this.validateDataBaseLaudo(true);
      const isArquivoValido = this.validateArquivoUpload(true);

      if (!isTituloValido || !isDataBaseValida || !isArquivoValido) {
        throw new Error(this.getFirstValidationMessage());
      }

      return {
        titulo: this.titulo.trim(),
        arquivo_xlsx_id: this.arquivoXlsxId,
        data_base_laudo: this.dataBaseLaudo
      };
    },

    validateTitulo(showError = true) {
      const isValid = !!this.titulo.trim();

      if (!isValid && showError) {
        this.fieldErrors.titulo = ['Informe o título do laudo express.'];
      }

      return isValid;
    },

    validateDataBaseLaudo(showError = true) {
      const isValid = !!this.dataBaseLaudo;

      if (!isValid && showError) {
        this.fieldErrors.data_base_laudo = ['Informe a Data Base do Laudo.'];
      }

      return isValid;
    },

    validateArquivoUpload(showError = true) {
      const isValid = !!this.arquivoXlsxId && !this.arquivoUploadUploading;

      if (!isValid && showError) {
        this.fieldErrors.arquivo_xlsx_id = ['Envie o arquivo .xlsx antes de registrar a execução.'];
      }

      return isValid;
    },

    validate() {
      return this.validateTitulo(false) && this.validateDataBaseLaudo(false) && this.validateArquivoUpload(false);
    },

    notifyValidationChange() {
      this.$nextTick(() => {
        this.$emit('validation-change', this.validate());
      });
    },

    clearArquivoUpload() {
      this.arquivoXlsxId = null;
      this.arquivoUploadFileObj = null;
      this.fieldErrors.arquivo_xlsx_id = [];
    },

    uploadNewFile(arquivo) {
      if (!arquivo) {
        this.clearArquivoUpload();
        return;
      }

      if (!/\.xlsx$/i.test(arquivo.name || '')) {
        this.clearArquivoUpload();
        this.fieldErrors.arquivo_xlsx_id = ['Apenas arquivos com extensão .xlsx são aceitos.'];
        this.$toast.error('Apenas arquivos .xlsx são aceitos.', '', { position: 'topRight' });
        return;
      }

      this.fieldErrors.arquivo_xlsx_id = [];
      this.arquivoXlsxId = null;
      this.arquivoUploadUploading = true;

      const formDataWithFile = new FormData();
      formDataWithFile.append('file', arquivo);

      MontagensAisIncrementaisService.uploadFile(formDataWithFile)
        .then((response) => {
          this.arquivoXlsxId = response.data.arquivoXlsxId;
          if (!this.titulo.trim()) {
            this.titulo = this.extractFileNameWithoutExtension(arquivo.name);
          }
          this.$toast.success('Arquivo enviado com sucesso.', '', { position: 'topRight' });
        })
        .catch((error) => {
          this.arquivoXlsxId = null;
          this.arquivoUploadFileObj = null;
          this.fieldErrors.arquivo_xlsx_id = [this.getUploadErrorMessage(error)];
          this.$toast.error(this.getUploadErrorMessage(error), '', { position: 'topRight' });
          console.error(error);
        })
        .finally(() => {
          this.arquivoUploadUploading = false;
        });
    },

    extractFileNameWithoutExtension(fileName = '') {
      return fileName.replace(/\.[^.]+$/, '');
    },

    getFirstValidationMessage() {
      const fields = ['titulo', 'data_base_laudo', 'arquivo_xlsx_id'];

      for (let i = 0; i < fields.length; i += 1) {
        const errors = this.fieldErrors[fields[i]];
        if (errors && errors.length > 0) {
          return errors[0];
        }
      }

      return 'Existem campos obrigatórios pendentes no formulário.';
    },

    getUploadErrorMessage(error) {
      if (error && error.response && error.response.data && error.response.data.erro) {
        return error.response.data.erro;
      }

      if (error && error.data && error.data.erro) {
        return error.data.erro;
      }

      return 'Erro no upload do arquivo.';
    }
  }
};
</script>
